import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Inbox, Mail, Calendar as Cal, Check, X, Sparkles, Loader2, BookOpen, Clock, Info,
} from "lucide-react";
import { api } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";

const DRAFT_ICON = { email: Mail, calendar: Cal };

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function Briefings() {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [morningSkill, setMorningSkill] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get("/proposals"), api.get("/skills")]);
      setProposals(p.data);
      setMorningSkill(s.data.find((x) => x.skill_kind === "morning_brief") || null);
    } catch {
      toast.error("Could not load briefings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    if (!morningSkill || running) return;
    setRunning(true);
    try {
      await api.post(`/skills/${morningSkill.id}/run`);
      await load();
      toast.success("New briefing ready.");
    } catch {
      toast.error("Couldn't generate the briefing.");
    } finally {
      setRunning(false);
    }
  };

  const updateDraft = async (pid, did, status) => {
    try {
      const r = await api.put(`/proposals/${pid}/drafts/${did}`, { status });
      setProposals((prev) => prev.map((p) => (p.id === pid ? { ...p, drafts: r.data.drafts } : p)));
    } catch {
      toast.error("Couldn't update the draft.");
    }
  };

  const saveToVault = async (pid) => {
    try {
      await api.post(`/proposals/${pid}/save-vault`);
      await load();
      toast.success("Saved to Vault → 01 Briefings.");
    } catch {
      toast.error("Couldn't save to Vault.");
    }
  };

  const dismiss = async (pid) => {
    try {
      await api.post(`/proposals/${pid}/dismiss`);
      setProposals((prev) => prev.map((p) => (p.id === pid ? { ...p, status: "dismissed" } : p)));
    } catch {
      toast.error("Couldn't dismiss.");
    }
  };

  const pending = proposals.filter((p) => p.status === "pending");
  const past = proposals.filter((p) => p.status !== "pending");

  return (
    <>
      <div className="surface-header">
        <div>
          <h2 style={{ margin: 0 }}>Briefings</h2>
          <div className="text-dim" style={{ fontSize: 13, marginTop: 2 }}>
            Scheduled skills propose drafts here — you decide what happens next.
          </div>
        </div>
        {morningSkill && (
          <button className="btn btn-primary" onClick={runNow} disabled={running} data-testid="run-brief-now-btn">
            {running ? <Loader2 size={15} className="spin" /> : <Sparkles size={15} />}
            {running ? "Generating…" : "Run Morning Brief now"}
          </button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "24px clamp(14px, 4vw, 32px) 60px" }} data-testid="briefings-surface">
        <div style={{ maxWidth: 780, margin: "0 auto" }}>
          <div className="brief-notice" data-testid="ms365-notice">
            <Info size={15} />
            <span>
              Drafts are prepared in-app from your data. <strong>Connect Microsoft 365</strong> to push approved
              email drafts to your Outlook inbox and add tentative calendar invites. Nothing is ever sent automatically.
            </span>
          </div>

          {loading ? (
            <div className="text-dim" style={{ padding: 24 }}>Loading…</div>
          ) : proposals.length === 0 ? (
            <div className="brief-empty fade-up" data-testid="briefings-empty">
              <Inbox size={30} className="text-dim" />
              <div style={{ fontWeight: 600, marginTop: 14 }}>No briefings yet</div>
              <div className="text-dim" style={{ fontSize: 13.5, marginTop: 6, maxWidth: 420 }}>
                Your Morning Email Brief runs weekday mornings at 7:00am. Want one now?
              </div>
              {morningSkill && (
                <button className="btn btn-primary" onClick={runNow} disabled={running} style={{ marginTop: 18 }} data-testid="run-brief-empty-btn">
                  {running ? <Loader2 size={15} className="spin" /> : <Sparkles size={15} />} Run Morning Brief now
                </button>
              )}
            </div>
          ) : (
            <>
              {pending.length > 0 && (
                <div className="section-label" style={{ marginBottom: 12 }}>Pending · {pending.length}</div>
              )}
              {pending.map((p) => (
                <ProposalCard key={p.id} p={p} onDraft={updateDraft} onSave={saveToVault} onDismiss={dismiss} navigate={navigate} />
              ))}

              {past.length > 0 && (
                <div className="section-label" style={{ margin: "26px 0 12px" }}>Earlier</div>
              )}
              {past.map((p) => (
                <ProposalCard key={p.id} p={p} onDraft={updateDraft} onSave={saveToVault} onDismiss={dismiss} navigate={navigate} muted />
              ))}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function ProposalCard({ p, onDraft, onSave, onDismiss, navigate, muted }) {
  return (
    <div className={`panel brief-card fade-up ${muted ? "muted" : ""}`} data-testid={`proposal-${p.id}`} style={{ padding: 22, marginBottom: 16, opacity: muted ? 0.72 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <span className="brief-skill-chip">{p.skill_name}</span>
        <span className="text-dim" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
          <Clock size={12} /> {timeAgo(p.created_at)}
        </span>
        {p.status === "reviewed" && <span className="text-cobalt" style={{ fontSize: 12 }}>· saved</span>}
        {p.status === "dismissed" && <span className="text-dim" style={{ fontSize: 12 }}>· dismissed</span>}
      </div>
      <h3 style={{ margin: "0 0 4px" }}>{p.title}</h3>
      {p.summary && <div className="text-soft" style={{ fontSize: 14, marginBottom: 12 }}>{p.summary}</div>}

      <Markdown text={p.body_md} />

      {p.drafts && p.drafts.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="section-label" style={{ marginBottom: 8 }}>Suggested drafts</div>
          {p.drafts.map((d) => {
            const Icon = DRAFT_ICON[d.kind] || Mail;
            return (
              <div key={d.id} className="brief-draft" data-testid={`proposal-draft-${d.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <Icon size={14} className="text-cobalt" />
                  <span style={{ fontWeight: 600, fontSize: 13.5 }}>{d.title}</span>
                  <span className="brief-draft-kind">{d.kind === "calendar" ? "calendar invite" : "email"}</span>
                  {d.status === "approved" && <span className="text-cobalt" style={{ fontSize: 12, marginLeft: "auto" }}><Check size={13} style={{ display: "inline" }} /> approved</span>}
                  {d.status === "dismissed" && <span className="text-dim" style={{ fontSize: 12, marginLeft: "auto" }}>dismissed</span>}
                </div>
                <div className="brief-draft-body">
                  {d.kind === "calendar" ? (
                    <>
                      {d.fields?.when && <div><span className="text-dim">When: </span>{d.fields.when}</div>}
                      {d.fields?.attendees && <div><span className="text-dim">Attendees: </span>{d.fields.attendees}</div>}
                      {d.fields?.notes && <div style={{ marginTop: 4 }}>{d.fields.notes}</div>}
                    </>
                  ) : (
                    <>
                      {d.fields?.to && <div><span className="text-dim">To: </span>{d.fields.to}</div>}
                      {d.fields?.subject && <div><span className="text-dim">Subject: </span>{d.fields.subject}</div>}
                      {d.fields?.body && <div style={{ marginTop: 6 }}>{d.fields.body}</div>}
                    </>
                  )}
                </div>
                {d.status === "pending" && p.status === "pending" && (
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button className="btn btn-ghost" onClick={() => onDraft(p.id, d.id, "dismissed")} data-testid={`draft-dismiss-${d.id}`}>
                      <X size={13} /> Dismiss
                    </button>
                    <button className="btn btn-primary" onClick={() => onDraft(p.id, d.id, "approved")} data-testid={`draft-approve-${d.id}`}>
                      <Check size={13} /> Approve draft
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          <div className="text-dim" style={{ fontSize: 11.5, marginTop: 6 }}>
            Approved drafts are kept here. Connect Microsoft 365 to push them to Outlook.
          </div>
        </div>
      )}

      {p.status === "pending" && (
        <div style={{ display: "flex", gap: 10, marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--hairline)" }}>
          <button className="btn" onClick={() => onSave(p.id)} data-testid={`brief-save-vault-${p.id}`}>
            <BookOpen size={14} /> Save to Vault
          </button>
          <button className="btn btn-ghost" onClick={() => onDismiss(p.id)} data-testid={`brief-dismiss-${p.id}`}>
            <X size={14} /> Dismiss briefing
          </button>
        </div>
      )}
      {p.vault_doc_id && (
        <button className="btn btn-ghost" onClick={() => navigate(`/vault/${p.vault_doc_id}`)} style={{ marginTop: 12 }} data-testid={`brief-open-vault-${p.id}`}>
          <BookOpen size={14} /> Open in Vault
        </button>
      )}
    </div>
  );
}
